import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import {
	Tooltip,
	TooltipArrow,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { convertYearToDate, extractYearFromDate, isValidYear } from '@/services/utils';

interface BirthdateProps {
	value?: string;
	onChange: (value: string) => void;
	idx?: number;
	showValidation?: boolean;
	disabled?: boolean;
}

const Birthdate = ({
	value,
	onChange,
	idx = 0,
	showValidation = false,
	disabled = false
}: BirthdateProps): React.JSX.Element => {
	const { t } = useTranslation();
	const [yearValue, setYearValue] = useState('');
	const [isValid, setIsValid] = useState(true);
	const [originalDate, setOriginalDate] = useState('');
	const [touched, setTouched] = useState(false);

	const validateYear = useCallback((year: string): boolean => {
		if (!year) return false;
		return year.length === 4 && isValidYear(year);
	}, []);

	useEffect(() => {
		const year = extractYearFromDate(value || '');
		setYearValue(year);
		setOriginalDate(value || '');
		const valid = validateYear(year);
		setIsValid(valid);
	}, [value, validateYear]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newYearValue = e.target.value;
		setYearValue(newYearValue);

		const valid = validateYear(newYearValue);
		setIsValid(valid);

		if (newYearValue) {
			if (newYearValue.length === 4 && isValidYear(newYearValue)) {
				const originalYear = extractYearFromDate(originalDate);
				const isOriginalDateSpecific =
					originalDate && originalDate !== convertYearToDate(originalYear);

				if (isOriginalDateSpecific && newYearValue === originalYear) {
					onChange(originalDate);
				} else {
					const fullDate = convertYearToDate(newYearValue);
					onChange(fullDate);
				}
			}
		} else {
			onChange('');
		}
	};

	const handleBlur = () => {
		setTouched(true);
		const valid = validateYear(yearValue);
		setIsValid(valid);
	};

	const showError = (touched || showValidation) && !isValid;
	const tooltipContent = showError ? t('quotes.settings.employees.birthdateTooltip') : '';

	return (
		<TooltipProvider>
			<Tooltip open={showError ? undefined : false}>
				<TooltipTrigger asChild>
					<div>
						<Input
							type="text"
							className={cn('h-8 text-[16px] sm:text-xs', showError && 'border-destructive')}
							value={yearValue}
							onChange={handleChange}
							onBlur={handleBlur}
							placeholder="YYYY"
							maxLength={4}
							disabled={disabled}
							data-testid={`employee-${idx}-birthdate-input`}
							required
						/>
					</div>
				</TooltipTrigger>
				{showError && tooltipContent && (
					<TooltipContent side="top">
						<p>{tooltipContent}</p>
						<TooltipArrow />
					</TooltipContent>
				)}
			</Tooltip>
		</TooltipProvider>
	);
};

export default Birthdate;
